import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  setUserProfile,
  GET_USER_PROFILE_MESSAGE_TYPE,
} from '@leagueplatform/user-profile-api';
import { toastActions, TOAST_STATUS } from '@leagueplatform/toast-messages';
import { clearNodeCache } from '@leagueplatform/masonry-engine';
import { MASONRY } from 'common/constants';

/**
 * Saves the member's preferred (first) name to the League user profile via
 * set_user_profile. On success the cached user profile is invalidated so the
 * Personal Info screen reflects the new value, the cached Home Masonry tree is
 * dropped so the Home greeting is refetched with the new name, and a success
 * toast is shown.
 * Failures surface only as the edit form's inline error (role="alert") — no
 * error toast, so the same message isn't shown twice.
 *
 * Returning the invalidation promise makes React Query await the refetch
 * before the mutate `onSuccess` callback runs — so the screen returns to the
 * read view only once the saved value is in the cache (no stale flash), and
 * the Save button stays in its loading state until then.
 */
export const useUpdatePreferredName = () => {
  const queryClient = useQueryClient();

  return useMutation<unknown, unknown, string>({
    mutationKey: ['updatePreferredName'],
    mutationFn: (preferredFirstName: string) =>
      setUserProfile({ preferred_first_name: preferredFirstName }),
    onSuccess: () => {
      toastActions.add({
        type: TOAST_STATUS.SUCCESS,
        textId: 'STR_PERSONAL_INFO_SAVE_SUCCESS',
      });
      clearNodeCache({
        id: MASONRY.ROOT_NODE_ID_HOME_PAGE,
        queryClient,
        self: true,
        descendants: true,
        strategy: 'remove',
      });
      return queryClient.invalidateQueries([GET_USER_PROFILE_MESSAGE_TYPE]);
    },
    // No error toast: the edit form already surfaces the failure inline
    // (role="alert"), so a toast would show the same message twice. Success has
    // no inline equivalent, hence the success toast above.
  });
};
